import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { auth } from "../../firebase";

export default function PricingSelection() {
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null); // 'trial' | 'pro' | null

  const handleSelectPlan = async (plan) => {
    try {
      if (!auth.currentUser) {
        alert("Please log in again.");
        return;
      }

      setLoadingPlan(plan);

      if (plan === "trial") {
        await api.post("/accountant/onboarding/select-plan", { plan });
        navigate("/accountant/select-provider");
      } else if (plan === "pro") {
        const res = await api.post(
          "/accountant/stripe/create-checkout-session",
          {
            uid: auth.currentUser.uid,
            email: auth.currentUser.email,
          }
        );
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error(err);
      alert("Failed to start plan. Please try again.");
      setLoadingPlan(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f9fafc",
        px: 2,
      }}
    >
      <Box maxWidth="1200px" width="100%" textAlign="center">
        <Typography variant="body2" color="text.secondary" mb={1}>
          Step 2 of 3: Choose Your Plan
        </Typography>

        <Typography variant="h4" fontWeight={700} gutterBottom>
          Choose Your Plan
        </Typography>

        <Typography variant="body1" color="text.secondary" mb={4}>
          Select the best plan for your accounting practice. You can change or
          cancel anytime.
        </Typography>

        <Grid
          container
          spacing={4}
          justifyContent="center"
          maxWidth="800px"
          mx="auto"
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.1)" },
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600}>
                  Free Trial
                </Typography>
                <Typography variant="h4" fontWeight={700} mt={1}>
                  €0
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  30 days free • Up to 5 clients • Unlimited receipts
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1} alignItems="flex-start">
                  <Typography variant="body2">✅ 5 clients</Typography>
                  <Typography variant="body2">✅ Unlimited receipts</Typography>
                  <Typography variant="body2">✅ Basic support</Typography>
                </Stack>
              </CardContent>
              <CardActions>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleSelectPlan("trial")}
                  disabled={loadingPlan === "trial"}
                  sx={{ py: 1 }}
                >
                  {loadingPlan === "trial" ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Start Free Trial"
                  )}
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                borderRadius: 2,
                border: "2px solid #2563eb",
                boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.1)" },
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600}>
                  Pro Plan{" "}
                  <Typography variant="caption" color="primary">
                    Most Popular
                  </Typography>
                </Typography>
                <Typography variant="h4" fontWeight={700} mt={1}>
                  €1,500
                  <Typography component="span" variant="h6" fontWeight={400}>
                    &nbsp;/ month
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Up to 100 clients • Priority support • Integrations
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1} alignItems="flex-start">
                  <Typography variant="body2">✅ 100 clients</Typography>
                  <Typography variant="body2">✅ Unlimited receipts</Typography>
                  <Typography variant="body2">✅ Priority support</Typography>
                  <Typography variant="body2">
                    ✅ QuickBooks integration
                  </Typography>
                  <Typography variant="body2">✅ Audit reports</Typography>
                </Stack>
              </CardContent>
              <CardActions>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleSelectPlan("pro")}
                  disabled={loadingPlan === "pro"}
                  sx={{ py: 1 }}
                >
                  {loadingPlan === "pro" ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Choose Pro Plan"
                  )}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          alignItems="center"
          mt={4}
        >
          <Typography variant="caption" color="text.secondary">
            ✅ Trusted by 100+ firms
          </Typography>
          <Typography variant="caption" color="text.secondary">
            🔒 SSL Secured Checkout
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ⭐ Rated 5 stars by early adopters
          </Typography>
        </Stack>

        <Typography variant="caption" color="text.secondary" mt={2}>
          You can upgrade, downgrade, or cancel anytime from your dashboard.
        </Typography>
      </Box>
    </Box>
  );
}
